import React, { useState, useEffect } from "react";
import ContinueButton from "../../Registration/ContinueButton";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import {
  updateLocationInfo,
  setBusinessLocation,
} from "../../store/supplierFormDataSlice";

const BusinessLocationForm = ({ onNext }) => {
  const dispatch = useDispatch();
  const [zipError, setZipError] = useState("");

  const businessLocation = useSelector((state) => state.formData.businessLocation || {});
  const supplierId = useSelector((state) => state.formData.supplierId);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    country: "USA",
    state: "",
    city: "",
    address_line_01: "",
    address_line_02: "",
    postal_code: "",
    ...businessLocation
  });

  const countries = [
    { code: "USA", name: "United States" },
    { code: "CAN", name: "Canada" },
  ];

  const statesUSA = [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
    "Washington, D.C."
  ];

  const provincesCanada = [
    "Alberta",
    "British Columbia",
    "Manitoba",
    "New Brunswick",
    "Newfoundland and Labrador",
    "Northwest Territories",
    "Nova Scotia",
    "Nunavut",
    "Ontario",
    "Prince Edward Island",
    "Quebec",
    "Saskatchewan",
    "Yukon",
  ];

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      textAlign: "start",
      background: "#F7F7F7",
      borderRadius: "0.5rem",
    }),
    control: (provided, state) => ({
      ...provided,
      border: "none",
      background: "#F9F9F9",
      textAlign: "start",
      height: "48px",
      borderRadius: "0.5rem",
      boxShadow: state.isFocused
        ? "0 0 0 2px rgba(128, 128, 128, 0.5)"
        : provided.boxShadow,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#1D2639",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "lightgray"
        : state.isFocused
        ? "lightgray"
        : provided.backgroundColor,
      color: "black",
      padding: "6px",
      borderRadius: "0.5rem",
      "&:active": {
        backgroundColor: "lightgray",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#B0B0B0",
    }),
  };

  const getStatesOrProvinces = (country) => {
    if (country === "USA") {
      return statesUSA;
    } else if (country === "CAN") {
      return provincesCanada;
    }
    return [];
  };

  const validatePostalCode = (zip, country) => {
    if (country === "USA") {
      const usZipRegex = /^\d{5}$/;
      return usZipRegex.test(zip)
        ? ""
        : "Please enter a valid 5-digit US ZIP code (e.g., 12345).";
    } else if (country === "CAN") {
      const canadaZipRegex = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/;
      return canadaZipRegex.test(zip)
        ? ""
        : "Please enter a valid Canadian Postal Code (e.g., K1A 0B1).";
    }
    return "";
  };

  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  };

  const debouncedPostalCode = useDebounce(formData.postal_code, 200);

  useEffect(() => {
    if (debouncedPostalCode) {
      setZipError(validatePostalCode(debouncedPostalCode, formData.country));
    } else {
      setZipError("");
    }
  }, [debouncedPostalCode]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (key === "postal_code" || key === "country") {
      setZipError("");
    }
  };

  const handleCountryChange = (value) => {
    setFormData({
      country: value,
      state: "",
      city: "",
      address_line_01: "",
      address_line_02: "",
      postal_code: "",
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    onNext();
  
    if (!supplierId) {
      setError("Supplier ID is missing. Please complete the basic information first.");
      return;
    }
  
    const hasDataChanged =
      formData.country !== businessLocation.country ||
      formData.state !== businessLocation.state ||
      formData.city !== businessLocation.city ||
      formData.address_line_01 !== businessLocation.address_line_01 ||
      formData.address_line_02 !== businessLocation.address_line_02 ||
      formData.postal_code !== businessLocation.postal_code;
  
    if (!hasDataChanged) {
      onNext();
      return; 
    }
  
    setIsSubmitting(true);
    setError(null);
  
    try {
      const locationInfo = {
        identifier: "primary",
        country: formData.country,
        state: formData.state,
        city: formData.city,
        street: formData.address_line_01,
        misc: "",
        address_line_01: formData.address_line_01,
        address_line_02: formData.address_line_02 || "",
        address_line_03: "",
        postal_code: formData.postal_code,
        division: "",
        zip_01: null,
        zip_02: null,
        latitude: "",
        longitude: ""
      };
  
      const payload = {
        id: supplierId,
        locationInfo: locationInfo
      };
  
      await dispatch(updateLocationInfo(payload)).unwrap();
      dispatch(setBusinessLocation(locationInfo));
      // onNext();
    } catch (error) {
      setError(error.message || "Failed to update location information");
      console.error("Failed to update location info:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const isDisabled = () => {
    const { country, state, city, address_line_01, postal_code } = formData;
    return !country || !state || !city || !address_line_01 || !postal_code;
  };

  return (
    <section className="flex flex-col items-center min-h-screen justify-start bg-white py-10 px-6">
      <header className="text-center mb-14">
        <h1 className="text-2xl font-semibold text-[#1D2639]">
          Business Location
        </h1>
        <p className="text-base font-medium text-[#7E7E7E] w-full sm:w-[572px] mt-2">
          Specify your primary business address. This information helps buyers
          understand your geographical presence and logistical capabilities,
          facilitating smoother collaborations.
        </p>
      </header>
      
      {error && (
        <div className="w-full sm:w-[572px] mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <form
        className="flex flex-col w-full sm:w-[572px]"
        onSubmit={handleSubmit}
      >
        <fieldset>
          <legend className="block text-[#1D2639] text-base text-start font-normal mb-2">
            Select Country:
          </legend>
          <div className="flex gap-4">
            {countries.map((country) => (
              <label
                key={country.code}
                className="flex text-[#1D2639] text-[12px] text-start font-normal gap-2 mb-2"
              >
                <input
                  type="radio"
                  name="country"
                  value={country.code}
                  checked={formData.country === country.code}
                  onChange={(e) => handleCountryChange(e.target.value)}
                />
                {country.name}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="mt-4">
          <label
            htmlFor="address_line_01"
            className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
          >
            Address <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="address_line_01"
            required
            placeholder="Enter Address"
            className="w-full h-12 px-4 bg-[#F9F9F9] border-2 border-gray-300 rounded-lg focus:outline-none"
            value={formData.address_line_01}
            onChange={(e) => handleChange("address_line_01", e.target.value)}
          />
        </div>
        <div className="mt-4">
          <label
            htmlFor="city"
            className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
          >
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="city"
            required
            placeholder="Enter City"
            className="w-full h-12 px-4 bg-[#F9F9F9] border-2 border-gray-300 rounded-lg focus:outline-none"
            value={formData.city}
            onChange={(e) => handleChange("city", e.target.value)}
          />
        </div>
        <div className="mt-4">
          <label
            htmlFor="state"
            className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
          >
            {formData.country === "USA" ? "State" : "Province"}{" "}
            <span className="text-red-500">*</span>
          </label>
          <Select
            id="state"
            name="state"
            options={getStatesOrProvinces(formData.country).map(
              (stateOrProvince) => ({
                label: stateOrProvince,
                value: stateOrProvince,
              })
            )}
            value={
              formData.state
                ? { label: formData.state, value: formData.state }
                : null
            }
            onChange={(selectedOption) =>
              handleChange("state", selectedOption.value)
            }
            styles={customStyles}
            placeholder={`Select ${
              formData.country === "USA" ? "State" : "Province"
            }`}
          />
        </div>
        <div className="flex flex-col gap-4">
          <div className="mt-4">
            <label
              htmlFor="postal_code"
              className="block text-[#1D2639] text-[12px] text-start font-normal mb-2"
            >
              {formData.country === "USA" ? "ZIP Code" : "Postal Code"}{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="postal_code"
              name="postal_code"
              required
              placeholder={
                formData.country === "USA"
                  ? "Enter ZIP Code (e.g., 12345)"
                  : "Enter Postal Code (e.g., K1A 0B1)"
              }
              className="w-full h-12 p-3 bg-[#F9F9F9] rounded-lg border-2 border-gray-300"
              value={formData.postal_code}
              onChange={(e) => handleChange("postal_code", e.target.value)}
            />
            {zipError && (
              <p className="text-red-500 text-sm text-start">{zipError}</p>
            )}
          </div>
        </div>
        <ContinueButton
          onClick={handleSubmit}
          label={isSubmitting ? "Saving..." : "Save and Continue"}
          isDisabled={isSubmitting || isDisabled()}
        />
      </form>
    </section>
  );
};

export default BusinessLocationForm;